import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Props as MultiSelectDropdownProps } from "@seneca/senekit/out/components/controls/MultiSelectDropdown";
import { Option } from "@seneca/senekit/out/components/controls/MultiSelectDropdown/types";
import { SemanticCategory } from "@seneca/tags-service-consts";

import { fetchAllTagsIfNotFetched } from "seneca-common/features/course-discovery/tags-feature/service";
import {
  getAllTags,
  getFetchTagsSuccess,
  getStateCategories
} from "seneca-common/features/course-discovery/tags-feature/state";
import { useDispatchOnMount } from "seneca-common/utils/hooks/dispatch";
import { useSelectorOnCondition } from "seneca-common/utils/hooks/selectors";

export type SubjectFilterReturn = ReturnType<typeof useSubjectFilter>;

type Tags = ReturnType<typeof getAllTags>;

type Props = {
  excludeHiddenTags?: boolean;
};

export default function useSubjectFilter({ excludeHiddenTags }: Props = {}) {
  useDispatchOnMount(fetchAllTagsIfNotFetched);

  const tagsFetched = useSelector(getFetchTagsSuccess);
  const tags = useSelectorOnCondition(tagsFetched, getAllTags);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const categories = useSelectorOnCondition(tagsFetched, getStateCategories);
  const subjectCategoryId = categories?.find(
    ({ semanticCategory }) => semanticCategory === SemanticCategory.subject
  )?.id;

  const [subjectsOptions, setSubjectsOptions] = useState<Option[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<Option[]>([]);
  useEffect(() => {
    if (tags && subjectCategoryId) {
      const options = getSubjectsOptionsFromTags({
        tags,
        subjectCategoryId,
        excludeHiddenTags
      });
      setSubjectsOptions(options);
      setSelectedSubjects(options);
    }
  }, [tags, subjectCategoryId, excludeHiddenTags]);

  function handleSetSelectedSubjects(value: MultiSelectDropdownProps["value"]) {
    const options = value as Option[];
    const isFiltered = ![0, subjectsOptions.length].includes(options.length);

    setSelectedSubjects(options);
    setIsFilterApplied(isFiltered);
  }

  return {
    tagsFetched,
    isFilterApplied,
    subjectsOptions,
    selectedSubjects,
    subjectCategoryId: subjectCategoryId ?? "",
    setSelectedSubjects: handleSetSelectedSubjects
  };
}

function getSubjectsOptionsFromTags({
  tags,
  subjectCategoryId,
  excludeHiddenTags
}: {
  tags: Tags;
  subjectCategoryId: string;
  excludeHiddenTags?: boolean;
}) {
  return tags
    ?.filter(({ id, text, parentId, details }) => {
      if (excludeHiddenTags && Boolean(details.get("hidden")) === true) {
        return false;
      }
      return parentId === subjectCategoryId && id && text;
    })
    .reduce(
      (acc: Option[], { id, text }) => acc.concat({ value: id!, label: text! }),
      []
    )
    .sort((a, b) => a.label.localeCompare(b.label));
}
